import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Shield } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/admin/login`, {
        email,
        password,
        totp_code: totpCode || undefined
      });

      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(response.data.temp_token);
        toast.info('Please enter your 2FA code');
      } else {
        localStorage.setItem('token', response.data.token);
        toast.success('Welcome back, Admin!');
        // Force page reload to update auth context
        window.location.href = '/admin';
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    }
    setLoading(false);
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        temp_token: tempToken,
        totp_code: totpCode
      });

      localStorage.setItem('token', response.data.token);
      toast.success('Welcome back, Admin!');
      // Force page reload to update auth context
      window.location.href = '/admin';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid 2FA code');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 bg-gradient-to-br from-pink-50/80 to-white">
      <Card className="w-full max-w-md border-pink-100/60 rounded-2xl shadow-soft">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-primary" strokeWidth={1.5} />
          </div>
          <CardTitle className="font-heading text-3xl">Admin Login</CardTitle>
          <CardDescription>Access the admin control panel</CardDescription>
        </CardHeader>
        <CardContent>
          {!requires2FA ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Admin Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@diorrussell.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-login-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-lift rounded-full"
                disabled={loading}
                data-testid="admin-login-submit"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Sign In as Admin
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerify2FA} className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="totp">2FA Code</Label>
                <Input
                  id="totp"
                  type="text"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                  required
                  data-testid="admin-2fa-code"
                />
              </div>
              <Button
                type="submit"
                className="w-full btn-lift"
                disabled={loading}
                data-testid="admin-2fa-submit"
              >
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Verify Code
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setRequires2FA(false);
                  setTotpCode('');
                }}
              >
                Back to Login
              </Button>
            </form>
          )}

          <p className="text-center text-sm text-muted-foreground mt-6">
            <Link to="/login" className="text-primary hover:underline">
              User Login
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
